/**
 * Referral API 接口
 * 用于获取推荐码页面和积分系统的数据
 */

// API配置
const API_CONFIG = {
  baseURL: window.API_BASE_URL, // 使用全局baseURL变量
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',

  }
};

// 模拟API延迟（开发环境）
const API_DELAY = 500; // 500ms

/**
 * 获取认证token (accessToken)
 * @returns {string} 认证token
 */
function getAuthToken() {
  if (!window.authAPI) {
    // 备用方法：直接从cookie获取 accessToken
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'accessToken') {
          return value || '';
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  return window.authAPI.getAccessToken();
}

/**
 * 通用API请求函数
 * @param {string} endpoint - API端点
 * @param {Object} options - 请求选项
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>} API响应
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_CONFIG.baseURL}${endpoint}`;
  const token = getAuthToken();

  const requestOptions = {
    method: options.method || 'GET',
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    },
    timeout: API_CONFIG.timeout,
    ...options
  };

  // 添加认证头
  if (token) {
    requestOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, requestOptions).catch(error => null);

  if (!response) {
    return { success: false, error: 'Network error, please check your connection' };
  }

  const data = await response.json().catch(() => null);
  
  if (!data) {
    return { success: false, error: 'Invalid response format' };
  }

  // 首先判断 code 是否为 200
  if (data.code !== 200) {
    return { success: false, error: data.message || 'please try again.' };
  }

  return { success: true, data: data.data, message: data.message };
}

/**
 * 获取用户积分数据
 * @returns {Promise<Object>} 积分数据
 */
async function getPointsData() {
  const response = await apiRequest('/user/points');
  return response;
}

/**
 * 获取推荐码数据
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>} 推荐码数据
 */
async function getReferralData() {
  const referralResponse = await apiRequest('/website/user/referral_reward_point');
  
  if (!referralResponse.success) {
    console.error('Error getting referral data:', referralResponse.error);
    return referralResponse;
  }
  
  const userProfileResponse = await apiRequest('/authority/account/profile');
  
  if (!userProfileResponse.success) {
    console.error('Error getting user profile:', userProfileResponse.error);
    return userProfileResponse;
  }
  
  // 合并数据
  const combinedData = {
    success: true,
    data: {
      user: {
        firstName: userProfileResponse.data?.firstName || 'User',
        referralCode: userProfileResponse.data?.invitationCode || 'DEFAULT'
      },
      bonus: {
        earned: referralResponse.data?.invitationCount || 0,
        nextInvite: referralResponse.data?.nextPersonCount || 0,
        invitesToNextTier: referralResponse.data?.peopleToNextTier || 0,
        currentTier: 'Bronze', // 暂时硬编码
        nextTier: 'Silver'     // 暂时硬编码
      },
      activity: {
        title: 'Get bonus toward your ev1',
        subtitle: 'Invite friends to FB Group!',
        startDate: '7/1/2025',
        endDate: '9/30/2025',
        isActive: true
      },
      progress: {
        currentInvites: referralResponse.data?.invitationNum || 0,
        totalInvitesForTier: 5, // 暂时硬编码
        progressPercentage: 0
      }
    }
  };

  return combinedData;
}

/**
 * 获取积分流转记录
 * @param {Object} params - 查询参数
 * @returns {Promise<Object>} 积分流转记录
 */
async function getPointsHistory(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const endpoint = `/user/points/history${queryString ? `?${queryString}` : ''}`;
  const response = await apiRequest(endpoint);
  return response;
}

/**
 * 格式化数字为千位分隔符格式
 * @param {number} num - 要格式化的数字
 * @returns {string} 格式化后的字符串
 */
function formatNumberWithCommas(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 格式化积分数据
 * @param {Object} rawData - 原始API数据
 * @returns {Object} 格式化后的数据
 */
function formatPointsData(rawData) {
  if (!rawData.success || !rawData.data) {
    console.error('Invalid points API response:', rawData);
    return null;
  }

  const data = rawData.data;

  return {
    currentPoints: formatNumberWithCommas(data.currentPoints),
    currentPointsRaw: data.currentPoints,
    expiringPoints: formatNumberWithCommas(data.expiringPoints),
    expiringPointsRaw: data.expiringPoints,
    expiringYear: data.expiringYear,
    pointsHistory: data.pointsHistory || []
  };
}

/**
 * 格式化referral数据用于显示
 * @param {Object} rawData - 原始API数据
 * @returns {Object} 格式化后的数据
 */
function formatReferralData(rawData) {
  if (!rawData.success || !rawData.data) {
    console.error('Invalid referral API response:', rawData);
    return null;
  }

  const data = rawData.data;

  return {
    user: {
      firstName: data.user.firstName,
      referralCode: data.user.referralCode
    },
    bonus: {
      earned: formatNumberWithCommas(data.bonus.earned),
      earnedRaw: data.bonus.earned,
      nextInvite: formatNumberWithCommas(data.bonus.nextInvite),
      nextInviteRaw: data.bonus.nextInvite,
      invitesToNextTier: data.bonus.invitesToNextTier,
      currentTier: data.bonus.currentTier,
      nextTier: data.bonus.nextTier
    },
    activity: {
      title: data.activity.title,
      subtitle: data.activity.subtitle,
      startDate: data.activity.startDate,
      endDate: data.activity.endDate,
      isActive: data.activity.isActive
    },
    progress: {
      currentInvites: data.progress.currentInvites,
      totalInvitesForTier: data.progress.totalInvitesForTier,
      progressPercentage: data.progress.progressPercentage
    }
  };
}

/**
 * 获取并格式化积分数据
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>} 格式化后的积分数据
 */
async function getFormattedPointsData() {
  const rawData = await getPointsData();
  
  if (!rawData.success) {
    return rawData;
  }
  
  const formattedData = formatPointsData(rawData);

  if (!formattedData) {
    return { success: false, error: 'Failed to format points data' };
  }

  return { success: true, data: formattedData };
}

/**
 * 获取并格式化referral数据
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>} 格式化后的referral数据
 */
async function getFormattedReferralData() {
  const rawData = await getReferralData();
  
  if (!rawData.success) {
    return rawData;
  }
  
  const formattedData = formatReferralData(rawData);

  if (!formattedData) {
    return { success: false, error: 'Failed to format referral data' };
  }

  return { success: true, data: formattedData };
}

// 导出API对象供外部使用
window.ReferralAPI = {
  // 原始API调用
  getPointsData,
  getReferralData,
  getPointsHistory,

  // 格式化数据
  getFormattedPointsData,
  getFormattedReferralData,

  // 工具函数
  formatNumberWithCommas,
  apiRequest
};

// 如果使用ES6模块，也可以这样导出
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    getPointsData,
    getReferralData,
    getPointsHistory,
    getFormattedPointsData,
    getFormattedReferralData,
    formatNumberWithCommas,
    apiRequest
  };
} 
